import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { JobDetail, JobInput, JobUpdateInput, JobStatus, JobCardStatus, JobInvoiceStatus } from '@jobos/shared/types/job';
import type { JobHoursReport } from '@jobos/shared/types/reports';
import { useToast } from '@/hooks/use-toast';

interface JobsResponse {
  jobs: JobDetail[];
}

interface JobDetailResponse {
  job: JobDetail;
}

interface JobFilters {
  status?: JobStatus[];
  jobCardStatus?: JobCardStatus[];
  invoiceStatus?: JobInvoiceStatus[];
  projectType?: string[];
  clientId?: string[];
  locationId?: string[];
  search?: string;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  isArchived?: boolean | 'only';
  showDeleted?: 'only';
  dateField?: string;
  dateFrom?: string;
  dateTo?: string;
}

// Query keys
export const jobKeys = {
  all: ['jobs'] as const,
  lists: () => [...jobKeys.all, 'list'] as const,
  list: (filters?: JobFilters) => [...jobKeys.lists(), filters] as const,
  details: () => [...jobKeys.all, 'detail'] as const,
  detail: (id: string) => [...jobKeys.details(), id] as const,
  hours: (id: string) => [...jobKeys.all, 'hours', id] as const,
};

// Fetch all jobs with optional filters
export function useJobs(filters?: JobFilters) {
  return useQuery({
    queryKey: jobKeys.list(filters),
    queryFn: async () => {
      const params: Record<string, string> = {};

      // Convert arrays to comma-separated strings
      if (filters?.status && filters.status.length > 0) {
        params.status = filters.status.join(',');
      }
      if (filters?.jobCardStatus && filters.jobCardStatus.length > 0) {
        params.job_card_status = filters.jobCardStatus.join(',');
      }
      if (filters?.invoiceStatus && filters.invoiceStatus.length > 0) {
        params.invoice_status = filters.invoiceStatus.join(',');
      }
      if (filters?.projectType && filters.projectType.length > 0) {
        params.project_type = filters.projectType.join(',');
      }
      if (filters?.clientId && filters.clientId.length > 0) {
        params.client_id = filters.clientId.join(',');
      }
      if (filters?.locationId && filters.locationId.length > 0) {
        params.location_id = filters.locationId.join(',');
      }
      if (filters?.search) {
        params.search = filters.search;
      }
      if (filters?.sortField) {
        params.sort_field = filters.sortField;
      }
      if (filters?.sortDirection) {
        params.sort_direction = filters.sortDirection;
      }
      if (filters?.isArchived !== undefined) {
        params.is_archived = filters.isArchived === 'only' ? 'only' :
                             filters.isArchived ? 'true' : 'false';
      }
      if (filters?.showDeleted === 'only') {
        params.show_deleted = 'only';
      }
      if (filters?.dateField) {
        params.date_field = filters.dateField;
      }
      if (filters?.dateFrom) {
        params.date_from = filters.dateFrom;
      }
      if (filters?.dateTo) {
        params.date_to = filters.dateTo;
      }

      const response = await apiClient.get<JobsResponse>('/jobs', {
        params: Object.keys(params).length > 0 ? params : undefined,
      });
      return response.jobs;
    },
    placeholderData: keepPreviousData,
  });
}

// Fetch single job by ID
export function useJob(id: string | undefined) {
  return useQuery({
    queryKey: jobKeys.detail(id!),
    queryFn: async () => {
      const response = await apiClient.get<JobDetailResponse>(`/jobs/${id}`);
      return response.job;
    },
    enabled: !!id,
  });
}

// Create job mutation
export function useCreateJob() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: JobInput) => {
      const response = await apiClient.post<JobDetailResponse>('/jobs', data);
      return response.job;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      toast({
        title: 'Success',
        description: 'Job created successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create job',
        variant: 'destructive',
      });
    },
  });
}

// Update job mutation
export function useUpdateJob(id: string, options?: { showToast?: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const showToast = options?.showToast ?? true;

  return useMutation({
    mutationFn: async (data: JobUpdateInput) => {
      const response = await apiClient.put<JobDetailResponse>(`/jobs/${id}`, data);
      return response.job;
    },
    onSuccess: (updatedJob) => {
      // Directly update the detail cache with the response (no refetch needed)
      queryClient.setQueryData(jobKeys.detail(id), updatedJob);

      // Update this job within any cached lists (no refetch needed)
      queryClient.setQueriesData<JobDetail[]>(
        { queryKey: jobKeys.lists() },
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.map(job => job.id === id ? { ...job, ...updatedJob } : job);
        }
      );

      if (showToast) {
        toast({
          title: 'Success',
          description: 'Job updated successfully',
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update job',
        variant: 'destructive',
      });
    },
  });
}

// Fetch job hours report
export function useJobHours(id: string | undefined) {
  return useQuery({
    queryKey: jobKeys.hours(id!),
    queryFn: async () => {
      const response = await apiClient.get<JobHoursReport>(`/reports/job-hours/${id}`);
      return response;
    },
    enabled: !!id,
  });
}

// Delete job mutation (soft delete with reason)
export function useDeleteJob() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, reason, userId }: { id: string; reason: string; userId?: string }) => {
      await apiClient.delete(`/jobs/${id}`, { reason, userId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      toast({
        title: 'Success',
        description: 'Job deleted successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete job',
        variant: 'destructive',
      });
    },
  });
}

// Archive job mutation
export function useArchiveJob(id: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (isArchived: boolean) => {
      const response = await apiClient.patch<JobDetailResponse>(
        `/jobs/${id}/archive`,
        { isArchived }
      );
      return response.job;
    },
    onSuccess: (_, isArchived) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      toast({
        title: 'Success',
        description: isArchived ? 'Job archived successfully' : 'Job unarchived successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update archive status',
        variant: 'destructive',
      });
    },
  });
}

// Duplicate job mutation (includes breakdown)
export function useDuplicateJob() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (sourceJob: JobDetail) => {
      const response = await apiClient.post<JobDetailResponse>(`/jobs/${sourceJob.id}/duplicate`);
      return response.job;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      toast({
        title: 'Success',
        description: 'Job duplicated successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to duplicate job',
        variant: 'destructive',
      });
    },
  });
}
